import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, ExternalLink, Image as ImageIcon, MessageCircleWarning, X } from "lucide-react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getQuoteByID, updateQuote } from "../../reducers/quote.slice";
import { AuthContext } from "../../context/authContext";
import { convertDateFormatInUtc, formatNumberToDollar } from "../../utils/util";
import PageHeader from "../../components/shared/PageHeader";
import StatusBadge from "../../components/shared/StatusBadge";
import useMediaQuery from "../../components/shared/useMediaQuery";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectOption } from "../../components/ui/select";

dayjs.extend(utc);
dayjs.extend(timezone);

const CHICAGO_TZ = "America/Chicago";
const PRICE_KEYS = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter", "Home", "End"];
const CLOSED_STATUSES = ["QUOTE_APPROVED", "QUOTE_ACCEPTED", "QUOTE_REJECTED", "QUOTE_DENIED"];

const rejectOptions = [
  "Sorry, we're currently not accepting this item.",
  "Unfortunately, we do not accept this brand at this time.",
  "We currently do not accept Pharmaceutical Products.",
  "This item can only be dropshipped. For more information, please open a ticket in our server.",
  "The UPC you entered is invalid.",
  "The item link you provided is invalid",
];

export const statusLabel = (status) => {
  const map = {
    QUOTE_PENDING: "Pending",
    QUOTE_APPROVED: "Approved",
    QUOTE_ACCEPTED: "Accepted",
    QUOTE_REJECTED: "Rejected",
    QUOTE_DENIED: "Denied",
  };
  return map[status] || status;
};

const formatAddress = (addr) => {
  if (!addr) return "";
  return [addr.address, addr.building, [addr.city, addr.state, addr.zipcode].filter(Boolean).join(", ")]
    .filter(Boolean)
    .join(" , ");
};

const dateTimeInputValue = (date) => {
  if (!date) return dayjs().tz(CHICAGO_TZ).add(2, "day").format("YYYY-MM-DDTHH:mm");
  return dayjs.tz(date, CHICAGO_TZ).format("YYYY-MM-DDTHH:mm");
};

const convertInputToCT = (value) => {
  if (!value) return null;
  return dayjs.tz(value, CHICAGO_TZ).format("YYYY-MM-DDTHH:mm:ss.SSSSSS");
};

const displayDateTime = (value) => {
  if (!value) return "-";
  return dayjs.tz(value, CHICAGO_TZ).format("MMM D, YYYY h:mm A");
};

const blockInvalidPrice = (e) => {
  const bad = ["e", "E", ",", "+", "-"];
  if (e.key === "." && e.target.value.includes(".")) {
    e.preventDefault();
    return;
  }
  if (
    bad.includes(e.key) ||
    (!/^\d$/.test(e.key) && e.key !== "." && !PRICE_KEYS.includes(e.key) && !e.ctrlKey && !e.metaKey)
  ) {
    e.preventDefault();
  }
};

export function QuoteImagePreview({ quote, size = 112 }) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(quote?.item_image_url || "");
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageSrc(quote?.item_image_url || "");
    setImageFailed(false);
  }, [quote?.item_image_url]);

  const fallbackPanel = (
    <span
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        width: "100%",
        height: "100%",
        color: "#8b9296",
        background: "rgba(255,255,255,0.04)",
        fontSize: 11,
        fontWeight: 700,
        textAlign: "center",
        padding: 10,
        boxSizing: "border-box",
      }}
    >
      <ImageIcon size={26} />
      Image unavailable
    </span>
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setPreviewOpen(true)}
        style={{
          width: size,
          height: size,
          borderRadius: 12,
          border: "1px solid rgba(255,255,255,0.08)",
          background: "rgba(255,255,255,0.025)",
          padding: 0,
          overflow: "hidden",
          cursor: "pointer",
          flexShrink: 0,
        }}
      >
        {imageSrc && !imageFailed ? (
          <img
            src={imageSrc}
            alt={quote?.item_name || "Quote item"}
            onError={() => setImageFailed(true)}
            style={{ width: "100%", height: "100%", objectFit: "contain", background: "rgba(255,255,255,0.04)" }}
          />
        ) : (
          fallbackPanel
        )}
      </button>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent style={{ maxWidth: 520, background: "rgba(0,0,0,0.92)", padding: 16 }}>
          {imageSrc && !imageFailed ? (
            <img
              src={imageSrc}
              alt={quote?.item_name || "Quote item"}
              style={{ width: "100%", borderRadius: 10, objectFit: "contain", maxHeight: "72vh", background: "#fff" }}
            />
          ) : (
            <div style={{ width: "100%", height: 260, borderRadius: 10, overflow: "hidden" }}>{fallbackPanel}</div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export function QuotePriceEditor({ cashoutPrice, setCashoutPrice, quantity, totalAmount, readOnly, error, setError }) {
  const numericPrice = Number(cashoutPrice || 0);
  const total = numericPrice * Number(quantity || 0);
  const displayTotal = total || Number(totalAmount || 0);

  if (readOnly) {
    return (
      <div style={{ minWidth: 0 }}>
        <ReadonlyLabelValue label="Cash Out Price (per unit)" value={formatNumberToDollar(numericPrice)} />
        <div style={{ display: "flex", justifyContent: "space-between", gap: 14, marginTop: 12, fontSize: 13 }}>
          <span style={{ color: "#c3c8cc" }}>Total Price</span>
          <span style={{ color: "#fff", fontFamily: "ui-monospace, monospace", fontWeight: 800 }}>
            {formatNumberToDollar(displayTotal)}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minWidth: 0 }}>
      <Label htmlFor="cashout_price" style={{ fontSize: 13 }}>
        Cash Out Price (per unit)
      </Label>
      <Input
        id="cashout_price"
        type="text"
        inputMode="decimal"
        placeholder="Price"
        value={cashoutPrice}
        readOnly={readOnly}
        onChange={(e) => {
          const value = e.target.value;
          if (value !== "" && Number(value) < 0) {
            setError("Price cannot be negative");
            setCashoutPrice("0");
            return;
          }
          setError("");
          setCashoutPrice(value);
        }}
        onKeyDown={blockInvalidPrice}
        onPaste={(e) => {
          if (!/^\d*\.?\d*$/.test(e.clipboardData.getData("text"))) e.preventDefault();
        }}
        style={{
          marginTop: 8,
          height: 40,
          borderRadius: 10,
          border: error ? "1px solid #f27059" : undefined,
          color: "#fff",
        }}
      />
      {error && <div style={{ color: "#f27059", fontSize: 11.5, marginTop: 6 }}>{error}</div>}
      <div style={{ display: "flex", justifyContent: "space-between", gap: 14, marginTop: 12, fontSize: 13 }}>
        <span style={{ color: "#c3c8cc" }}>Total Price</span>
        <span style={{ color: "#fff", fontFamily: "ui-monospace, monospace", fontWeight: 700 }}>
          {formatNumberToDollar(total)}
        </span>
      </div>
    </div>
  );
}

export function QuoteExpiryField({ value, setValue, readOnly }) {
  if (readOnly) {
    return <ReadonlyLabelValue label="Quote Expiry Date" value={displayDateTime(value)} helper="Central time" />;
  }

  return (
    <div style={{ minWidth: 0 }}>
      <Label htmlFor="quote_expiry" style={{ fontSize: 13 }}>
        {readOnly ? "Quote Expiry Date" : "Enter Quote Expiry Date"}
      </Label>
      <Input
        id="quote_expiry"
        type="datetime-local"
        value={value}
        readOnly={readOnly}
        min={dayjs().tz(CHICAGO_TZ).format("YYYY-MM-DDTHH:mm")}
        onChange={(e) => setValue(e.target.value)}
        style={{
          marginTop: 8,
          height: 40,
          borderRadius: 10,
          colorScheme: "dark",
        }}
      />
      <div style={{ color: "#8b9296", fontSize: 11.5, marginTop: 6 }}>Central time</div>
    </div>
  );
}

function ReadonlyLabelValue({ label, value, helper }) {
  return (
    <div style={{ minWidth: 0 }}>
      <Label style={{ fontSize: 13 }}>{label}</Label>
      <div
        style={{
          marginTop: 8,
          minHeight: 40,
          display: "flex",
          alignItems: "center",
          borderRadius: 10,
          border: "1px solid rgba(255,255,255,0.08)",
          background: "rgba(255,255,255,0.018)",
          color: "#fff",
          fontSize: 13.5,
          fontWeight: 700,
          padding: "0 14px",
          overflowWrap: "anywhere",
        }}
      >
        {value || "-"}
      </div>
      {helper && <div style={{ color: "#8b9296", fontSize: 11.5, marginTop: 6 }}>{helper}</div>}
    </div>
  );
}

export function QuoteDecisionDialogs({
  quote,
  rejection,
  setRejection,
  approveOpen,
  setApproveOpen,
  rejectOpen,
  setRejectOpen,
  onApprove,
  onReject,
  submitting,
}) {
  return (
    <>
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent style={{ maxWidth: 460 }}>
          <DialogHeader>
            <DialogTitle>Confirm Quote Approval</DialogTitle>
            <DialogDescription>
              {`You are about to approve this quote for ${quote?.item_name}. Once approved, the member will be notified to either accept or reject the quote.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter style={{ gap: 8 }}>
            <Button variant="outline" onClick={() => setApproveOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={onApprove}
              disabled={submitting}
              style={{ background: "#4caf50", color: "#fff" }}
            >
              {submitting ? "Approving..." : "Approve Quote"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent style={{ maxWidth: 460 }}>
          <DialogHeader>
            <DialogTitle>Confirm Quote Rejection</DialogTitle>
            <DialogDescription>
              You are about to reject this quote for {quote?.item_name}. Once rejected, the member will be notified of your decision.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="rejection">Reason of Rejection</Label>
            <Select
              id="rejection"
              value={rejection}
              onChange={(e) => setRejection(e.target.value)}
              style={{ marginTop: 8 }}
            >
              <SelectOption value="">Select a reason...</SelectOption>
              {rejectOptions.map((opt) => (
                <SelectOption key={opt} value={opt}>
                  {opt}
                </SelectOption>
              ))}
            </Select>
          </div>
          <DialogFooter style={{ gap: 8 }}>
            <Button variant="outline" onClick={() => setRejectOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={onReject} disabled={submitting}>
              {submitting ? "Rejecting..." : "Reject Quote"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function FieldGrid({ fields }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12 }}>
      {fields.map(({ label, value }) =>
        value !== null && value !== undefined && value !== "" ? (
          <div
            key={label}
            style={{
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 10,
              padding: "10px 12px",
              background: "rgba(255,255,255,0.018)",
              minWidth: 0,
            }}
          >
            <div style={{ color: "#8b9296", fontSize: 10, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase" }}>
              {label}
            </div>
            <div style={{ color: "#fff", fontSize: 13, marginTop: 5, overflowWrap: "anywhere" }}>{value}</div>
          </div>
        ) : null
      )}
    </div>
  );
}

const sectionTitleStyle = {
  color: "#fff",
  fontSize: 14,
  fontWeight: 800,
  margin: "0 0 14px",
};

const freeSectionStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 0,
};

export default function PendingQuoteDetailContent({
  quote,
  quoteId,
  compact = false,
  showBack = false,
  dialogMode = false,
  onUpdated,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const [quoteData, setQuoteData] = useState(quote || {});
  const [loading, setLoading] = useState(Boolean(quoteId && !quote));
  const [submitting, setSubmitting] = useState(false);
  const [cashoutPrice, setCashoutPrice] = useState(quote?.cashout_price ?? "");
  const [priceError, setPriceError] = useState("");
  const [rejection, setRejection] = useState("");
  const [expiryDate, setExpiryDate] = useState(dateTimeInputValue(quote?.quote_expiry));
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  useEffect(() => {
    if (quote) {
      setQuoteData(quote);
      setCashoutPrice(quote?.cashout_price ?? "");
      setExpiryDate(dateTimeInputValue(quote?.quote_expiry));
      setPriceError("");
    }
  }, [quote?.id]);

  useEffect(() => {
    if (!quoteId || quote) return;
    setLoading(true);
    dispatch(getQuoteByID(quoteId)).then((res) => {
      if (res?.error && Object.keys(res.error).length > 0) {
        setLoading(false);
        showToast(res.error.message, "error");
        return;
      }
      const payload = res?.payload || {};
      setQuoteData({
        ...payload,
        createdDate: convertDateFormatInUtc(payload?.created_at, "MMMM D, YYYY"),
      });
      setCashoutPrice(payload?.cashout_price ?? "");
      setExpiryDate(dateTimeInputValue(payload?.quote_expiry));
      setLoading(false);
    });
  }, [dispatch, quoteId]);

  const isReadOnly = CLOSED_STATUSES.includes(quoteData?.status);
  const numericPrice = Number(cashoutPrice || 0);
  const quantity = Number(quoteData?.quantity || 0);
  const totalPrice = numericPrice * quantity;
  const approveDisabled = isReadOnly || !cashoutPrice || numericPrice === 0 || Boolean(priceError) || submitting;

  const summaryFields = useMemo(
    () => [
      { label: "Quote Number", value: quoteData?.id },
      {
        label: "Member Name",
        value: quoteData?.user_details
          ? `${quoteData.user_details.first_name || ""} ${quoteData.user_details.last_name || ""}`.trim()
          : null,
      },
      { label: "Quote Date", value: quoteData?.createdDate || convertDateFormatInUtc(quoteData?.created_at, "MMMM D, YYYY") },
      {
        label: "Status",
        value: quoteData?.status ? <StatusBadge status={quoteData.status} label={statusLabel(quoteData.status)} /> : null,
      },
      {
        label: "Shipping Address",
        value: quoteData?.user_details?.user_shipping_address
          ? formatAddress(quoteData.user_details.user_shipping_address)
          : null,
      },
    ],
    [quoteData]
  );

  const itemFields = [
    { label: "UPC", value: quoteData?.upc },
    { label: "Quantity", value: quoteData?.quantity },
    {
      label: "Item Link",
      value: quoteData?.item_link ? (
        <a
          href={quoteData.item_link}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#00ADB5", display: "inline-flex", alignItems: "center", gap: 5 }}
        >
          View item <ExternalLink size={12} />
        </a>
      ) : null,
    },
  ];

  const handleUpdateQuote = (action) => {
    const payload = {
      id: quoteData?.id,
      status: action,
      cashout_price: parseFloat(cashoutPrice),
      total_amount: parseFloat(totalPrice),
      notes: rejection,
      quote_expiry: expiryDate ? convertInputToCT(expiryDate) : null,
    };
    if (action === "QUOTE_APPROVED") delete payload.notes;
    if (action === "QUOTE_REJECTED") {
      delete payload.cashout_price;
      delete payload.total_amount;
      if (!rejection) delete payload.notes;
    }

    setSubmitting(true);
    dispatch(updateQuote(payload)).then((res) => {
      if (res?.error && Object.keys(res.error).length > 0) {
        setSubmitting(false);
        showToast(res.error.message, "error");
        return;
      }
      setSubmitting(false);
      setApproveOpen(false);
      setRejectOpen(false);
      showToast("Quote updated successfully", "success");
      onUpdated?.();
      if (showBack) navigate(-1);
    });
  };

  if (loading) {
    return <div style={{ padding: "40px 0", textAlign: "center", color: "#c3c8cc", fontSize: 13 }}>Loading quote...</div>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: compact ? 14 : 18 }}>
      {showBack && (
        <button
          type="button"
          onClick={() => navigate(-1)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            alignSelf: "flex-start",
            background: "transparent",
            border: "none",
            color: "#c3c8cc",
            cursor: "pointer",
            fontSize: 13,
            padding: 0,
          }}
        >
          <ArrowLeft size={15} /> Back
        </button>
      )}

      {showBack && !dialogMode && (
        <PageHeader title="Pending RSC Quote" subtitle="Review quote details, pricing, expiry, and decision history." />
      )}

      {quoteData?.notes && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            padding: "12px 14px",
            borderRadius: 12,
            border: "1px solid rgba(242,112,89,0.26)",
            background: "rgba(242,112,89,0.08)",
          }}
        >
          <MessageCircleWarning size={17} color="#f27059" style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <div style={{ color: "#f27059", fontSize: 13, fontWeight: 800 }}>Reason For Denial</div>
            <div style={{ color: "#fff", fontSize: 13, marginTop: 4, lineHeight: 1.5 }}>{quoteData.notes}</div>
          </div>
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile
            ? "1fr"
            : compact
              ? "minmax(96px, 120px) minmax(0, 1fr)"
              : "minmax(120px, 160px) minmax(0, 1fr)",
          gap: compact ? 16 : 22,
          alignItems: "stretch",
        }}
      >
        <QuoteImagePreview quote={quoteData} size={compact ? 108 : 150} />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: 14,
            minWidth: 0,
          }}
        >
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <h2 style={{ margin: 0, color: "#fff", fontSize: compact ? 15 : 20, fontWeight: 800, lineHeight: 1.25, overflowWrap: "anywhere" }}>
                {quoteData?.item_name}
              </h2>
              {quoteData?.status && <StatusBadge status={quoteData.status} label={statusLabel(quoteData.status)} />}
            </div>
            {!compact && (
              <p style={{ color: "#c3c8cc", fontSize: 13, lineHeight: 1.5, margin: "8px 0 0" }}>
                Quote #{quoteData?.id || "-"} · {quoteData?.quantity || 0} unit{Number(quoteData?.quantity) === 1 ? "" : "s"}
              </p>
            )}
          </div>

          <FieldGrid fields={itemFields} />
        </div>
      </div>

      {!compact && (
        <section style={freeSectionStyle}>
          <h3 style={sectionTitleStyle}>Quote Summary</h3>
          <FieldGrid fields={summaryFields} />
        </section>
      )}

      <section style={freeSectionStyle}>
        <h3 style={sectionTitleStyle}>Quote Decision</h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: compact && !isMobile ? "repeat(3, minmax(0, 1fr))" : "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 16,
            }}
          >
            <QuotePriceEditor
              cashoutPrice={cashoutPrice}
              setCashoutPrice={setCashoutPrice}
              quantity={quantity}
              totalAmount={quoteData?.total_amount}
              readOnly={isReadOnly}
              error={priceError}
              setError={setPriceError}
            />
            <QuoteExpiryField value={expiryDate} setValue={setExpiryDate} readOnly={isReadOnly} />
            <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", gap: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 14, fontSize: 13 }}>
                <span style={{ color: "#c3c8cc" }}>Current Total</span>
                <span style={{ color: "#4caf50", fontWeight: 800, fontFamily: "ui-monospace, monospace" }}>
                  {formatNumberToDollar(totalPrice || quoteData?.total_amount || 0)}
                </span>
              </div>
              {quoteData?.status === "QUOTE_PENDING" && (
                <div style={{ display: "flex", justifyContent: compact ? "flex-start" : "flex-end", gap: 8, flexWrap: "wrap" }}>
                  <Button
                    variant="outline"
                    onClick={() => setRejectOpen(true)}
                    style={{ color: "#f27059", borderColor: "rgba(242,112,89,0.45)" }}
                  >
                    <X size={14} /> Reject
                  </Button>
                  <Button
                    onClick={() => setApproveOpen(true)}
                    disabled={approveDisabled}
                    style={{ background: "#4caf50", color: "#fff" }}
                  >
                    <Check size={14} /> Approve
                  </Button>
                </div>
              )}
            </div>
          </div>
      </section>

      <QuoteDecisionDialogs
        quote={quoteData}
        rejection={rejection}
        setRejection={setRejection}
        approveOpen={approveOpen}
        setApproveOpen={setApproveOpen}
        rejectOpen={rejectOpen}
        setRejectOpen={setRejectOpen}
        onApprove={() => handleUpdateQuote("QUOTE_APPROVED")}
        onReject={() => handleUpdateQuote("QUOTE_REJECTED")}
        submitting={submitting}
      />
    </div>
  );
}
